import React from "react";
import { BrowserRouter, Routes as RouterRoutes, Route, useLocation } from "react-router-dom";
import ScrollToTop from "./components/ScrollToTop";
import ErrorBoundary from "./components/ErrorBoundary";
import ProtectedRoute from "./components/ProtectedRoute";
import NotFound from "./pages/NotFound";
import { getUserId, getAuthToken } from './utils/cookies';
import { useAuth } from "./contexts/AuthContext";
import ProfileProgress from './pages/profile-progress';
import Footer from './components/ui/Footer';
import CoachWidget from './components/CoachWidget';
import { useFeatureFlags } from "./contexts/FeatureFlagsContext";
import LandingPage from './pages/landing-page';
// Pilot disabled: public reviews page retained for post-pilot restore.
// import ReviewPage from './pages/review';
import ForBusinessesPage from './pages/for-businesses';
import ForInstitutionsPage from './pages/for-institutions';
import InstitutionApp from './institution/InstitutionApp';
import BusinessApp from './business/BusinessApp';
import BusinessInterviewsPage from './pages/business-interviews';
import LiveInterviewPanel from './pages/live-interview-panel';
import Dashboard from './pages/dashboard';
import InterviewSetup from './pages/interview-setup';
import AssignedInterviews from './pages/institution-interviews';
import InterviewReport from './pages/interview-report';
import LoginPage from './pages/login';
import RegisterPage from './pages/register';
import ForgotPasswordPage from './pages/forgot-password';
import ResetPasswordPage from './pages/reset-password';
import AuthCallback from './pages/auth-callback';
import PreInterviewCheck from './pages/pre-interview-check';
import GeneratingResult from './pages/generating-result';
import Onboarding from './pages/onboarding';
import Settings from './pages/settings';
import SocialPage from './pages/social';
import PublicProfile from './pages/public-profile';
import Checkout from './pages/checkout';
import SupportPage from './pages/support';
import AdminInvite from './pages/AdminInvite';
import InterviewCalendar from './pages/interview-calendar';
// Pilot disabled: structured/AI plan pages.
// import MyPlan from './pages/my-plan';
// import AiPlanQuestionnaire from './pages/ai-plan-questionnaire';
import BlogsList from './pages/blogs-list';
import BlogDetail from './pages/blog-detail';
import BlogSubmit from './pages/blog-submit';
import BlogAnalytics from './pages/blog-analytics';
import LearnIndex from './pages/learn/index';
import ArticleReader from './pages/learn/ArticleReader';
import Quiz from './pages/learn/Quiz';
import Leaderboard from './pages/leaderboard';
import Jobs from './pages/jobs/index';
import RankingSystemPage from './pages/ranking-system';


import CommunityForum from './pages/community/index';
import PostDetail from './pages/community/PostDetail';
import CreatePost from './pages/community/CreatePost';
import EditPost from './pages/community/EditPost';
import MyPosts from './pages/community/MyPosts';

import Blocked from './pages/Blocked';
import ResumeAnalyzer from './pages/ats-resume-score';
// Pilot disabled: public certificate page.
// import CertificatePage from './pages/certificate';
import CodePlaygroundPage from './pages/playground/CodePlaygroundPage';

import Terms from './pages/legal/Terms';
import PrivacyPolicy from './pages/legal/PrivacyPolicy';
import RefundPolicy from './pages/legal/RefundPolicy';
import CookiePolicy from './pages/legal/CookiePolicy';
import InterviewConsent from './pages/legal/InterviewConsent';
import BetaDisclaimer from './pages/legal/BetaDisclaimer';



const FeatureGate = ({ feature, children, fallback = <NotFound /> }) => {
  const { isEnabled, loading } = useFeatureFlags();
  if (loading) return null;
  return isEnabled(feature) ? children : fallback;
};

const AppContent = () => {
  const location = useLocation();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const { isEnabled, loading: flagsLoading } = useFeatureFlags();
  
  const loading = authLoading || flagsLoading;
  
  const hideFooter = location.pathname === '/live-interview-panel' || location.pathname === '/blocked' || location.pathname === '/' || location.pathname === '/landing-page' || location.pathname === '/for-businesses' || location.pathname.startsWith('/for-businesses/interviews') || location.pathname === '/for-institutions' || location.pathname.startsWith('/institution') || location.pathname.startsWith('/business') || location.pathname === '/login' || location.pathname === '/register' || location.pathname.startsWith('/resume-analyzer') || location.pathname === '/onboarding';

  const alwaysHideCoachPaths = ['/', '/landing-page', '/review', '/reviews', '/for-businesses', '/for-institutions', '/login', '/register', '/forgot-password', '/reset-password', '/resume-analyzer', '/blocked', '/onboarding'];
  const alwaysHideCoachPrefixes = ['/interview', '/auth', '/certificate', '/live', '/for-businesses/interviews', '/resume-analyzer/', '/institution', '/business'];
  const isHiddenPath = alwaysHideCoachPaths.includes(location.pathname) || alwaysHideCoachPrefixes.some(p => location.pathname.startsWith(p));
  
  // Only show coach if authenticated and not on a hidden path.
  // We wait for BOTH auth and feature flags to load to prevent jitter.
  const hideCoach = loading || !isAuthenticated || isHiddenPath;

  return (
    <>
      <div className="flex flex-col min-h-screen bg-background text-foreground">

      <main className="flex-1 flex flex-col">
      <RouterRoutes>
        {/* ──────────── PUBLIC ROUTES ──────────── */}
        <Route path="/" element={<LandingPage />} />
        <Route path="/landing-page" element={<LandingPage />} />
        {/* Pilot disabled: reviews page hidden during restricted pilot.
        <Route path="/review" element={<ReviewPage />} />
        <Route path="/reviews" element={<ReviewPage />} />
        */}
        <Route path="/for-businesses" element={<ForBusinessesPage />} />
        <Route path="/for-institutions" element={<ForInstitutionsPage />} />
        {/* Institution admin console — self-contained descendant app with its own
            auth boundary (bearer token). Not wrapped by the consumer's
            ProtectedRoute; it gates itself. Runs on this same origin/port. */}
        <Route path="/institution/*" element={<InstitutionApp />} />
        {/* Business hiring console — self-contained descendant app with its own
            auth boundary (bearer token). Not wrapped by the consumer's
            ProtectedRoute; it gates itself. Runs on this same origin/port. */}
        <Route path="/business/*" element={<BusinessApp />} />
        <Route path="/for-businesses/interviews" element={<BusinessInterviewsPage />} />
        <Route path="/for-businesses/interviews/:roomId/feedback" element={<BusinessInterviewsPage />} />
        <Route path="/for-businesses/interviews/:roomId" element={<BusinessInterviewsPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/blocked" element={<Blocked />} />
        <Route path="/admin-invite" element={<AdminInvite />} />
        <Route path="/users/profile/:username" element={<PublicProfile />} />

        {/* Blogs — public read */}
        <Route path="/blogs" element={<FeatureGate feature="blog"><BlogsList /></FeatureGate>} />
        <Route path="/blogs/:slug" element={<FeatureGate feature="blog"><BlogDetail /></FeatureGate>} />

        {/* Learn — public */}
        <Route path="/learn" element={<FeatureGate feature="learn"><LearnIndex /></FeatureGate>} />
        <Route path="/learn/:categorySlug" element={<FeatureGate feature="learn"><LearnIndex /></FeatureGate>} />
        <Route path="/learn/:categorySlug/:articleSlug" element={<FeatureGate feature="learn"><ArticleReader /></FeatureGate>} />
        <Route path="/learn/quiz/:categorySlug" element={<FeatureGate feature="learn"><Quiz /></FeatureGate>} />

        {/* Community — public read (like/comment gated at component level) */}
        <Route path="/community" element={<FeatureGate feature="community"><CommunityForum /></FeatureGate>} />
        <Route path="/community/post/:id" element={<FeatureGate feature="community"><PostDetail /></FeatureGate>} />

        {/* Resume Analyzer — public */}
        <Route path="/resume-analyzer" element={<FeatureGate feature="ats"><ResumeAnalyzer /></FeatureGate>} />
        <Route path="/resume-analyzer/:reportId" element={<FeatureGate feature="ats"><ResumeAnalyzer /></FeatureGate>} />

        {/* Jobs — public */}
        <Route path="/jobs" element={<FeatureGate feature="jobs"><Jobs /></FeatureGate>} />

        {/* Ranking Method — public */}
        <Route path="/ranking-system" element={<RankingSystemPage />} />

        {/* Certificate — public */}
        {/* Pilot disabled: certificates are hidden for the restricted pilot.
        <Route path="/certificate/:sessionId" element={<CertificatePage />} />
        */}

        {/* Legal — public */}
        <Route path="/terms" element={<Terms />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/refund" element={<RefundPolicy />} />
        <Route path="/cookies" element={<CookiePolicy />} />
        <Route path="/interview-consent" element={<InterviewConsent />} />
        <Route path="/beta-disclaimer" element={<BetaDisclaimer />} />

        {/* ──────────── PROTECTED ROUTES (login required) ──────────── */}
        <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
        <Route path="/profile" element={<ProtectedRoute><ProfileProgress /></ProtectedRoute>} />
        <Route path="/profile-progress" element={<ProtectedRoute><ProfileProgress /></ProtectedRoute>} />
        <Route path="/interview-setup" element={<ProtectedRoute><InterviewSetup /></ProtectedRoute>} />
        <Route path="/assigned-interviews" element={<ProtectedRoute><AssignedInterviews /></ProtectedRoute>} />
        <Route path="/pre-interview-check" element={<ProtectedRoute><PreInterviewCheck /></ProtectedRoute>} />
        <Route path="/live-interview-panel" element={<ProtectedRoute><LiveInterviewPanel /></ProtectedRoute>} />
        <Route path="/interview-report" element={<ProtectedRoute><InterviewReport /></ProtectedRoute>} />
        <Route path="/generating-result" element={<ProtectedRoute><GeneratingResult /></ProtectedRoute>} />
        {/* <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} /> */}
        {/* <Route path="/support" element={<ProtectedRoute><SupportPage /></ProtectedRoute>} /> */}
        <Route path="/social" element={<ProtectedRoute><SocialPage /></ProtectedRoute>} />
        <Route path="/checkout" element={<ProtectedRoute><Checkout /></ProtectedRoute>} />
        <Route path="/sandbox" element={<ProtectedRoute><CodePlaygroundPage /></ProtectedRoute>} />
        <Route path="/interview-calendar" element={<ProtectedRoute><InterviewCalendar /></ProtectedRoute>} />
        {/*
        <Route path="/my-plan" element={<ProtectedRoute><MyPlan /></ProtectedRoute>} />
        <Route path="/ai-plan-questionnaire/:planId" element={<ProtectedRoute><AiPlanQuestionnaire /></ProtectedRoute>} />
        */}
        <Route path="/blog-submit" element={<FeatureGate feature="blog"><ProtectedRoute><BlogSubmit /></ProtectedRoute></FeatureGate>} />
        <Route path="/blog-analytics/:id" element={<FeatureGate feature="blog"><ProtectedRoute><BlogAnalytics /></ProtectedRoute></FeatureGate>} />
        <Route path="/leaderboard" element={<FeatureGate feature="leaderboard"><ProtectedRoute><Leaderboard /></ProtectedRoute></FeatureGate>} />


        {/* Community write routes — login required */}
        <Route path="/community/create" element={<FeatureGate feature="community"><ProtectedRoute><CreatePost /></ProtectedRoute></FeatureGate>} />
        <Route path="/community/post/:id/edit" element={<FeatureGate feature="community"><ProtectedRoute><EditPost /></ProtectedRoute></FeatureGate>} />
        <Route path="/community/my-posts" element={<FeatureGate feature="community"><ProtectedRoute><MyPosts /></ProtectedRoute></FeatureGate>} />

        <Route path="*" element={<NotFound />} />
      </RouterRoutes>
      </main>
      {!hideFooter && <Footer />}
      </div>
      {!hideCoach && <CoachWidget />}
    </>
  );
};

const Routes = () => {
  return (
    <ErrorBoundary>
      <ScrollToTop />
      <AppContent />
    </ErrorBoundary>
  );
};

export default Routes;
